import * as React from 'react';
import { Popover } from '@base-ui-components/react/popover';
import { Box, TextField, Typography } from "@mui/material";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultButton from '../buttons/DefaultButton';
import { API_BASE } from '../../../services/api';
import { useAuth } from '../../../services/auth';
import { postAuthDestination } from '../../../services/postAuthRedirect';
import { colors } from '../../../theme';
import { pillTriggerStyle, popupStyle, positionerStyle, closeButtonStyle, backdropStyle } from '../buttons/triggerStyles';

interface SignupProps {
    /** Lets the landing page reuse this same modal for its hero/closing CTAs. */
    label?: string;
    size?: "medium" | "large";
}

export default function Signup({ label = "Sign up", size = "medium" }: SignupProps) {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { onAuthenticated } = useAuth();

    // Shown against the field itself as soon as the two diverge, rather than waiting
    // for a submit to reject it. Held back until they've actually started confirming,
    // so the form doesn't flag an error on a field they haven't reached yet.
    const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

    const handleSubmit = () => {
        if (!username || !email || !password || !confirmPassword) {
            setError('All fields are required.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setError('');
        // Signup reads validation messages off the error body, so it keeps its own
        // fetch rather than using apiFetch (which throws before the body is read).
        fetch(`${API_BASE}/v1/users/signup`, {
            method: 'POST',
            // Required, not incidental: signup's response now carries the httpOnly session
            // cookie, and a cross-origin Set-Cookie is discarded unless the request that
            // produced it asked for credentials. Without this the signup looks like it
            // worked and the user is signed out on their very next request.
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password }),
        }).then(response => {
            if (!response.ok) {
                return response.json().then(err => {
                    setError(err.message || 'Failed to sign up. Please try again.');
                });
            }
            response.json().then(data => {
                // The cookie is already set by the response; this just re-checks who we are.
                onAuthenticated();
                navigate(postAuthDestination());
            });
        }).catch(err => {
            setError('Network error: ' + err.message);
        });
    };

    return (
        <Popover.Root>
            <Popover.Trigger style={pillTriggerStyle('primary', size)}>
                {label}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Backdrop style={backdropStyle} />
                <Popover.Positioner style={positionerStyle}>
                    <Popover.Popup style={popupStyle}>
                        <Popover.Close aria-label="Close" style={closeButtonStyle}>
                            ✕
                        </Popover.Close>

                        <Typography variant="h4" sx={{ mb: 0.5 }}>Sign up</Typography>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Free to start. Bring your own streaming account.
                        </Typography>

                        <TextField
                            label="Username"
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            label="Email"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            label="Password"
                            type="password"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            label="Confirm password"
                            type="password"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={passwordsMismatch}
                            helperText={passwordsMismatch ? "Passwords do not match." : " "}
                            sx={{ mb: 1.5 }}
                        />

                        {error && (
                            <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                {error}
                            </Typography>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <DefaultButton
                                text="Create account"
                                onClick={handleSubmit}
                                disabled={passwordsMismatch}
                                fullWidth
                            />
                        </Box>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
